/**
 * The chain's own answer to "what will this block?" — ROADMAP 4.19.
 *
 * `estimateMargin` is the model; this is the adapter that feeds it from what
 * the terminal already has on screen — the resolved legs, the implied
 * volatilities `useChainGreeks` solved in one pass, and the forward it
 * solved them against.
 *
 * ONE PASS, ONE FORWARD. The greeks hook solves every strike off a single
 * forward from the at-the-money straddle, and the margin scan reuses exactly
 * that. Re-deriving a forward here would margin the basket against a
 * slightly different underlying from the one its greeks describe, and the
 * difference would look like a model disagreement rather than two clocks.
 */

import { estimateMargin, type MarginEstimate, type MarginLeg } from '@apna/shared'
import { RISK_FREE_RATE, type ChainGreeks } from './useChainGreeks'

/** The parts of a resolved leg margin needs. */
export interface MarginableLeg {
  readonly token: number
  readonly side: 'BUY' | 'SELL'
  readonly optionType: 'CE' | 'PE'
  readonly strike: number
  /** Lots for THIS leg — the ratio is already in it. */
  readonly lots: number
  /** Per-unit value, for the premium a long basket pays. */
  readonly price: number
}

export interface MarginContext {
  readonly greeks: ChainGreeks
  readonly spot: number
  readonly lotSize: number
  readonly rate?: number
  /** True when the chain's expiry is today. */
  readonly isExpiryDay: boolean
}

/**
 * Initial margin for a basket of resolved legs.
 *
 * Refuses, with a reason, when any leg has no solved volatility — see the
 * model's header. A margin number for a basket we cannot revalue is a
 * specific claim about blocked capital with nothing behind it.
 */
export function marginForLegs(
  legs: readonly MarginableLeg[],
  ctx: MarginContext,
): MarginEstimate {
  if (legs.length === 0) return { ok: false, why: 'no legs' }
  if (!(ctx.lotSize > 0)) return { ok: false, why: 'no lot size for this contract' }
  if (ctx.greeks.forward === null) {
    return { ok: false, why: 'no at-the-money straddle yet, so there is no forward to scan against' }
  }

  const marginLegs: MarginLeg[] = []
  for (const leg of legs) {
    const solved = ctx.greeks.byToken.get(leg.token)
    if (!solved) {
      return {
        ok: false,
        why: ctx.greeks.refusals.get(leg.token) ?? 'a leg has no implied volatility yet',
      }
    }
    const units = leg.lots * ctx.lotSize
    marginLegs.push({
      netQty: leg.side === 'BUY' ? units : -units,
      strike: leg.strike,
      optionType: leg.optionType,
      iv: solved.iv,
      price: leg.price,
    })
  }

  return estimateMargin({
    legs: marginLegs,
    forward: ctx.greeks.forward,
    spot: ctx.spot,
    years: ctx.greeks.years,
    // The same rate the greeks were solved with, for the same reason.
    rate: ctx.rate ?? RISK_FREE_RATE,
    isExpiryDay: ctx.isExpiryDay,
  })
}
